import { getUrl } from '@aws-amplify/storage';

/**
 * Parse supplemental documents from JSON string or object
 * @param {string|object|null} supplementalCoiFiles - The supplemental documents data
 * @returns {Array} Array of document objects
 */
export const parseSupplementalDocuments = (supplementalCoiFiles) => {
  if (!supplementalCoiFiles) return [];
  
  try {
    const data = typeof supplementalCoiFiles === 'string' 
      ? JSON.parse(supplementalCoiFiles) 
      : supplementalCoiFiles;
    
    // Handle array format (new format)
    if (Array.isArray(data)) {
      return data;
    }
    
    // Handle nested object format (legacy format)
    // Format: {"AU":{"Endorsement":"DortCOI.pdf"},"UMB":{"Subrogation Waiver":"DortCOI.pdf"}}
    if (typeof data === 'object' && data !== null) {
      const documents = [];
      Object.keys(data).forEach(policyType => {
        if (typeof data[policyType] === 'object') {
          Object.entries(data[policyType]).forEach(([docType, filename]) => {
            documents.push({
              id: `${policyType}-${docType}-${filename}`,
              filename: filename,
              fileKey: filename,
              policyTypes: [policyType],
              documentType: docType,
              uploadedDate: null,
              fileSize: null
            });
          });
        }
      });
      return documents;
    }
    
    return [];
  } catch (e) {
    console.error('Error parsing supplemental documents:', e);
    return [];
  }
};

/**
 * Get signed URLs for supplemental documents
 * @param {string} certificateId - The certificate ID
 * @param {Array} documents - Array of document objects
 * @returns {Promise<Array>} Array of documents with signed URLs
 */
export const getSupplementalDocumentUrls = async (certificateId, documents) => {
  if (!documents || documents.length === 0) return [];
  
  try {
    const documentsWithUrls = await Promise.all(
      documents.map(async (doc) => {
        try {
          // Determine the correct path based on whether it's from supplementalCoiFiles or files array
          let path;
          if (doc.fileKey && doc.fileKey.includes('supplemental/')) {
            // New supplemental format
            path = `protected/certificates/${certificateId}/${doc.fileKey}`;
          } else {
            // Regular files array or just filename
            path = `protected/certificates/${certificateId}/${doc.fileKey || doc.filename}`;
          }
          
          const { url } = await getUrl({ path });
          
          return {
            ...doc,
            fileUrl: url.toString(),
          };
        } catch (error) {
          console.error(`Error getting URL for document ${doc.filename}:`, error);
          return {
            ...doc,
            fileUrl: null,
          };
        }
      })
    );
    
    return documentsWithUrls;
  } catch (error) {
    console.error('Error getting supplemental document URLs:', error);
    return documents;
  }
};

/**
 * Format file size from bytes to human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (!bytes) return '-';
  
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  if (bytes === 0) return '0 Bytes';
  
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Download a document file
 * @param {object} document - Document object with fileUrl
 * @param {string} filename - Optional filename override
 */
export const downloadDocument = async (document, filename = null) => {
  if (!document.fileUrl) {
    console.error('No file URL available for download');
    return;
  }
  
  try {
    const response = await fetch(document.fileUrl);
    const blob = await response.blob();
    
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || document.filename || 'document.pdf';
    
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    
    // Clean up
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error downloading document:', error);
  }
};